import { SecureStorageEcho, type SecureStorageEchoObject } from "@awesome-cordova-plugins/secure-storage-echo";
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { trackErrorEvent, trackEvent } from "../utils/analytics";

/** Storage key for the auth refresh token. Centralized so every reader/writer agrees. */
export const REFRESH_TOKEN_STORAGE_KEY = "refresh_token";

interface IStorage {
	set(key: string, value: string): Promise<void>;
	get(key: string): Promise<string | null>;
	remove(key: string): Promise<void>;
	clear(): Promise<void>;
	getName(): string;
}

class PreferencesStorage implements IStorage {
	getName() {
		return "Preferences";
	}

	async set(key: string, value: string) {
		await Preferences.set({ key, value });
	}

	async get(key: string) {
		return (await Preferences.get({ key })).value;
	}

	async remove(key: string) {
		await Preferences.remove({ key });
	}

	async clear() {
		await Preferences.clear();
	}
}
class SecureStorageEchoStorage implements IStorage {
	private instance: SecureStorageEchoObject | null = null;
	private readonly storageName: string;

	constructor(storageName: string) {
		this.storageName = storageName;
	}

	getName() {
		return "SecureStorageEcho";
	}

	private async getInstance() {
		this.instance ??= await SecureStorageEcho.create(this.storageName);
		return this.instance;
	}

	async set(key: string, value: string) {
		await (await this.getInstance()).set(key, value);
	}

	async get(key: string) {
		const instance = await this.getInstance();
		try {
			return await instance.get(key);
		} catch (error) {
			const message = error instanceof Error ? error.message : String(error);
			if (/not found|could not be found/i.test(message)) return null;
			throw error;
		}
	}

	async remove(key: string) {
		await (await this.getInstance()).remove(key);
	}

	async clear() {
		await (await this.getInstance()).clear();
	}
}
class StorageService {
	private primaryStorage: IStorage | null = null;
	private fallbackStorage: IStorage | null = null;
	private readonly STORAGE_NAME = "inhouse_auth";
	private initializationPromise: Promise<void> | null = null;

	private logError(method: string, error: any, key?: string) {
		const context = {
			platform: Capacitor.getPlatform(),
			isNative: Capacitor.isNativePlatform(),
			storageMethod: method,
			...(key && { key }),
		};

		trackErrorEvent(`Storage failure: ${method}`, error, context);
	}

	private async tryStorage(storage: IStorage): Promise<boolean> {
		try {
			await storage.set("_test", "test");
			await storage.remove("_test");
			return true;
		} catch (error) {
			this.logError(`${storage.getName()} initialization failed`, error);
			return false;
		}
	}

	private async _performInitialization() {
		if (!Capacitor.isNativePlatform()) return;

		// Try Preferences first
		const preferences = new PreferencesStorage();
		if (await this.tryStorage(preferences)) {
			this.primaryStorage = preferences;
			this.fallbackStorage = new SecureStorageEchoStorage(this.STORAGE_NAME);
			return;
		}

		// Fallback to SecureStorageEcho
		const secure = new SecureStorageEchoStorage(this.STORAGE_NAME);
		if (await this.tryStorage(secure)) {
			this.primaryStorage = secure;
			this.fallbackStorage = null;
			return;
		}

		// Both failed
		this.primaryStorage = null;
		this.fallbackStorage = null;
	}

	private async initialize() {
		if (this.primaryStorage !== null) return;

		if (this.initializationPromise) {
			return this.initializationPromise;
		}

		this.initializationPromise = this._performInitialization();
		await this.initializationPromise;
		this.initializationPromise = null;
	}

	private async tryWithFallback<T>(operation: (storage: IStorage) => Promise<T>, key: string, defaultValue: T): Promise<T> {
		await this.initialize();
		if (!this.primaryStorage) return defaultValue;

		try {
			return await operation(this.primaryStorage);
		} catch (error) {
			this.logError(`${this.primaryStorage.getName()} failed`, error, key);

			if (this.fallbackStorage) {
				try {
					const result = await operation(this.fallbackStorage);
					trackEvent("Storage fallback used", {
						primary: this.primaryStorage.getName(),
						fallback: this.fallbackStorage.getName(),
						key,
					});
					return result;
				} catch {}
			}
			return defaultValue;
		}
	}

	async set(key: string, value: string): Promise<boolean> {
		const result = await this.tryWithFallback(
			async (storage) => {
				await storage.set(key, value);
				return true;
			},
			key,
			false,
		);
		if (!result) {
			trackErrorEvent("storage_error set failed", { message: "No storage available" }, { context: { key } });
		}
		return result;
	}

	async get(key: string): Promise<string | null> {
		const value = await this.tryWithFallback((storage) => storage.get(key), key, null);

		// Check fallback for legacy data migration
		if (!value && this.primaryStorage && this.fallbackStorage) {
			try {
				const fallbackValue = await this.fallbackStorage.get(key);
				if (fallbackValue) {
					trackEvent("Legacy data found", {
						key,
						from: this.fallbackStorage.getName(),
						to: this.primaryStorage.getName(),
					});
					try {
						await this.primaryStorage.set(key, fallbackValue);
					} catch (migrationError) {
						// Migration write is best-effort, but a stuck migration must not go unseen.
						this.logError(`${this.primaryStorage.getName()} legacy migration write failed`, migrationError, key);
					}
					return fallbackValue;
				}
			} catch (fallbackError) {
				this.logError(`${this.fallbackStorage.getName()} legacy read failed`, fallbackError, key);
			}
		}

		return value;
	}

	/** Like get(), but reports whether the read THREW (readFailed) vs the key being genuinely absent — so callers don't mistake a transient read glitch for "no token". */
	async getWithReadStatus(key: string): Promise<{ value: string | null; readFailed: boolean }> {
		await this.initialize();
		if (!this.primaryStorage) {
			trackEvent("storage_read_failed", { key, reason: "no_storage_available", platform: Capacitor.getPlatform() });
			return { value: null, readFailed: true };
		}

		try {
			const value = await this.primaryStorage.get(key);
			if (value) return { value, readFailed: false };

			// Primary empty — mirror get()'s legacy-data migration from the fallback store.
			if (this.fallbackStorage) {
				try {
					const fallbackValue = await this.fallbackStorage.get(key);
					if (fallbackValue) {
						try {
							await this.primaryStorage.set(key, fallbackValue);
						} catch (migrationError) {
							this.logError(`${this.primaryStorage.getName()} legacy migration write failed`, migrationError, key);
						}
						return { value: fallbackValue, readFailed: false };
					}
				} catch (fallbackError) {
					this.logError(`${this.fallbackStorage.getName()} legacy read failed`, fallbackError, key);
					trackEvent("storage_read_failed", { key, platform: Capacitor.getPlatform(), reason: "fallback_legacy_read_failed" });
					return { value: null, readFailed: true };
				}
			}
			return { value: null, readFailed: false };
		} catch (primaryError) {
			this.logError(`${this.primaryStorage.getName()} read failed`, primaryError, key);
			// A read failure is not "absent" — try the fallback before concluding it failed.
			if (this.fallbackStorage) {
				try {
					const fallbackValue = await this.fallbackStorage.get(key);
					if (fallbackValue) return { value: fallbackValue, readFailed: false };
				} catch (fallbackError) {
					this.logError(`${this.fallbackStorage.getName()} read failed`, fallbackError, key);
				}
			}
			trackEvent("storage_read_failed", { key, platform: Capacitor.getPlatform() });
			return { value: null, readFailed: true };
		}
	}

	async remove(key: string) {
		await this.initialize();
		if (!this.primaryStorage) return;

		try {
			await this.primaryStorage.remove(key);
		} catch (error) {
			this.logError(`${this.primaryStorage.getName()} remove failed`, error, key);
		}

		// Best-effort cleanup of legacy/migrated copies so they don't zombie back via
		// get()'s legacy-data path. Fallback failures are non-critical here — set/get
		// surface real fallback issues via tryWithFallback's logging.
		await this.fallbackStorage?.remove(key).catch(() => {});
	}

	async clear() {
		await this.initialize();

		if (this.primaryStorage) {
			await this.primaryStorage.clear().catch((error) => {
				trackErrorEvent("storage_error clear failed", error, {
					context: { storage: this.primaryStorage?.getName() },
				});
			});
		}

		if (this.fallbackStorage) {
			await this.fallbackStorage.clear().catch((error) => {
				trackErrorEvent("storage_error clear failed", error, {
					context: { storage: this.fallbackStorage?.getName() },
				});
			});
		}
	}

	getStorageMethod() {
		return this.primaryStorage?.getName() || null;
	}

	isStorageUnavailable() {
		return this.primaryStorage === null;
	}
}

export const storageService = new StorageService();

export const readRefreshToken = (): Promise<{ value: string | null; readFailed: boolean }> => storageService.getWithReadStatus(REFRESH_TOKEN_STORAGE_KEY);
